/**
 * Parser for markdown-structured campaign report data
 * Extracts structured data from markdown text format
 */

import { CampaignReportData, CampaignMeta, Kpis, Conversions, Revenue, Retention, Halo, TimeOfDay, Feedback, StrategicTakeaways } from '../types/campaignReport';
import { parseNumber, parsePercentage } from './reportFormatters';

/**
 * Parse markdown text into structured CampaignReportData
 */
export function parseCampaignReportMarkdown(markdown: string): CampaignReportData | null {
  if (!markdown || typeof markdown !== 'string') {
    return null;
  }

  try {
    const lines = markdown.split('\n');
    
    // Parse header/metadata
    const meta = parseMeta(lines);
    
    // Parse KPIs
    const kpis = parseKpis(lines);
    
    // Parse conversions
    const conversions = parseConversions(lines);
    
    // Parse revenue
    const revenue = parseRevenue(lines);
    
    // Parse retention
    const retention = parseRetention(lines);
    
    // Parse halo effect
    const halo = parseHalo(lines);
    
    // Parse time of day
    const timeOfDay = parseTimeOfDay(lines);
    
    // Parse feedback
    const feedback = parseFeedback(lines);
    
    // Parse strategic takeaways
    const strategicTakeaways = parseStrategicTakeaways(lines);
    
    return {
      meta,
      kpis,
      conversions,
      revenue,
      retention,
      halo,
      timeOfDay,
      feedback,
      strategicTakeaways,
    };
  } catch (error) {
    console.error('Error parsing campaign report markdown:', error);
    return null;
  }
}

/**
 * Parse metadata from header section
 */
function parseMeta(lines: string[]): CampaignMeta {
  const meta: Partial<CampaignMeta> = {
    location: '',
    duration: '',
    product: '',
    price: '',
    productStructure: [],
  };
  
  let inProductStructure = false;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    // Parse header fields
    if (line.startsWith('**Location:**')) {
      meta.location = line.replace('**Location:**', '').replace(/\\/g, '').trim();
    } else if (line.startsWith('**Duration:**')) {
      meta.duration = line.replace('**Duration:**', '').replace(/\\/g, '').trim();
    } else if (line.startsWith('**Product:**')) {
      const productLine = line.replace('**Product:**', '').replace(/\\/g, '').trim();
      // Extract price from product line (e.g., "£120 Lush Experience Box")
      const priceMatch = productLine.match(/£(\d+)/);
      if (priceMatch) {
        meta.price = `£${priceMatch[1]}`;
        meta.product = productLine.replace(/£\d+\s*/, '').trim();
      } else {
        meta.product = productLine;
      }
    }
    
    // Parse Product Structure section
    if (line.includes('Product Structure')) {
      inProductStructure = true;
      continue;
    }
    
    if (inProductStructure) {
      if (line.startsWith('-')) {
        const structureItem = line.replace(/^-\s*/, '').replace(/\\/g, '').trim();
        if (structureItem && !structureItem.includes('---')) {
          meta.productStructure!.push(structureItem);
        }
      } else if (line.includes('---') || line.includes('Campaign Performance')) {
        inProductStructure = false;
      }
    }
  }
  
  return meta as CampaignMeta;
}

/**
 * Parse KPIs from Footfall & Visibility and Engagement sections
 */
function parseKpis(lines: string[]): Kpis {
  const kpis: Partial<Kpis> = {
    footfall: { sixMonthTotal: 0 },
    screenImpressions: { sixMonthTotal: 0 },
    interactions: { sixMonthTotal: 0 },
    browsingSessions: { sixMonthTotal: 0 },
    totalUnitsSold: 0,
    totalCampaignRevenue: 0,
  };
  
  let inFootfallSection = false;
  let inEngagementSection = false;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    if (line.includes('Footfall & Visibility')) {
      inFootfallSection = true;
      inEngagementSection = false;
      continue;
    }
    
    if (line.includes('Engagement Metrics')) {
      inFootfallSection = false;
      inEngagementSection = true;
      continue;
    }
    
    if (line.includes('---') && (inFootfallSection || inEngagementSection)) {
      inFootfallSection = false;
      inEngagementSection = false;
    }
    
    // Parse Footfall & Visibility table
    if (inFootfallSection && line.includes('Footfall (zone)')) {
      const parts = line.split(/\s{2,}/);
      if (parts.length >= 3) {
        kpis.footfall!.monthlyAverage = parseNumber(parts[1]);
        kpis.footfall!.sixMonthTotal = parseNumber(parts[2]);
      }
    }
    
    if (inFootfallSection && line.includes('Screen Impressions')) {
      const parts = line.split(/\s{2,}/);
      if (parts.length >= 3) {
        kpis.screenImpressions!.monthlyAverage = parseNumber(parts[1]);
        kpis.screenImpressions!.sixMonthTotal = parseNumber(parts[2]);
      }
    }
    
    if (inFootfallSection && line.includes('Ad Playouts')) {
      const match = line.match(/~?(\d+[,.]?\d*)\s*(?:total\s*)?plays/i);
      if (match) {
        kpis.adPlayouts = {
          description: '6 ads rotating',
          totalPlays: parseNumber(match[1]),
        };
      }
    }
    
    // Parse Engagement Metrics table
    if (inEngagementSection && line.includes('Screen Clicks') || line.includes('Interactions')) {
      const parts = line.split(/\s{2,}/);
      if (parts.length >= 3) {
        kpis.interactions!.monthlyAverage = parseNumber(parts[1]);
        kpis.interactions!.sixMonthTotal = parseNumber(parts[2]);
      }
    }
    
    if (inEngagementSection && line.includes('Browsing Sessions')) {
      const parts = line.split(/\s{2,}/);
      if (parts.length >= 3) {
        kpis.browsingSessions!.monthlyAverage = parseNumber(parts[1]);
        kpis.browsingSessions!.sixMonthTotal = parseNumber(parts[2]);
      }
    }
    
    if (inEngagementSection && line.includes('Average Browse Time')) {
      const match = line.match(/(\d+)\s*seconds/i);
      if (match) {
        kpis.averageBrowseTime = parseInt(match[1]);
      }
    }
    
    // Parse Footfall → Browse Rate
    if (line.includes('Footfall → Browse Rate')) {
      const match = line.match(/(\d+\.?\d*)%/);
      if (match) {
        kpis.footfallToBrowseRate = parsePercentage(match[1]);
      }
    }
  }
  
  // Parse total units and revenue from other sections
  const conversions = parseConversions(lines);
  kpis.totalUnitsSold = conversions.totalUnitsSold;
  
  const revenue = parseRevenue(lines);
  kpis.totalCampaignRevenue = revenue.totalCampaignRevenue;
  
  return kpis as Kpis;
}

/**
 * Parse conversion metrics
 */
function parseConversions(lines: string[]): Conversions {
  const conversions: Partial<Conversions> = {
    browseToPurchaseRate: 0,
    footfallToPurchaseRate: 0,
    averageSellingPrice: 0,
    totalUnitsSold: 0,
  };
  
  let inConversionSection = false;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    if (line.includes('Conversion Performance')) {
      inConversionSection = true;
      continue;
    }
    
    if (line.includes('---') && inConversionSection) {
      inConversionSection = false;
    }
    
    if (inConversionSection) {
      if (line.includes('Footfall → Purchase')) {
        const match = line.match(/(\d+\.?\d*)%/);
        if (match) {
          conversions.footfallToPurchaseRate = parsePercentage(match[1]);
        }
      }
      
      if (line.includes('Browsing → Purchase')) {
        const match = line.match(/(\d+\.?\d*)%/);
        if (match) {
          conversions.browseToPurchaseRate = parsePercentage(match[1]);
        }
      }
      
      if (line.includes('Average Selling Price')) {
        const match = line.match(/£(\d+)/);
        if (match) {
          conversions.averageSellingPrice = parseNumber(match[1]);
        }
      }
      
      if (line.includes('Monthly Units Sold')) {
        const parts = line.split(/\s{2,}/);
        if (parts.length >= 2) {
          conversions.monthlyUnitsSold = parseNumber(parts[parts.length - 1]);
        }
      }
      
      if (line.includes('Total Units') && line.includes('6 months')) {
        const parts = line.split(/\s{2,}/);
        if (parts.length >= 2) {
          conversions.totalUnitsSold = parseNumber(parts[parts.length - 1]);
        }
      }
    }
  }
  
  return conversions as Conversions;
}

/**
 * Parse revenue metrics
 */
function parseRevenue(lines: string[]): Revenue {
  const revenue: Partial<Revenue> = {
    initialBox: { sixMonthTotal: 0 },
    subscription: { sixMonthTotal: 0 },
    totalCampaignRevenue: 0,
  };
  
  let inRevenueSection = false;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    if (line.includes('Revenue Performance')) {
      inRevenueSection = true;
      continue;
    }
    
    if (line.includes('---') && inRevenueSection) {
      inRevenueSection = false;
    }
    
    if (inRevenueSection) {
      if (line.includes('Initial Box Revenue')) {
        const parts = line.split(/\s{2,}/);
        if (parts.length >= 3) {
          revenue.initialBox!.monthly = parseNumber(parts[1]);
          revenue.initialBox!.sixMonthTotal = parseNumber(parts[2]);
        }
      }
      
      if (line.includes('Subscription Revenue')) {
        const parts = line.split(/\s{2,}/);
        const descriptionMatch = line.match(/\(([^)]+)\)/);
        if (descriptionMatch) {
          revenue.subscription!.description = descriptionMatch[1];
        }
        if (parts.length >= 3) {
          revenue.subscription!.monthly = parseNumber(parts[1]);
          revenue.subscription!.sixMonthTotal = parseNumber(parts[2]);
        }
      }
      
      if (line.includes('Total Campaign Revenue') && line.includes('**')) {
        const parts = line.split(/\s{2,}/);
        if (parts.length >= 3) {
          revenue.totalCampaignRevenue = parseNumber(parts[parts.length - 1]);
        }
      }
      
      if (line.includes('Total Transaction Value Processed')) {
        const match = line.match(/£([\d.]+)M/i);
        if (match) {
          revenue.totalTransactionValueProcessed = parseFloat(match[1]) * 1000000;
        } else {
          const parts = line.split(/\s{2,}/);
          if (parts.length >= 2) {
            revenue.totalTransactionValueProcessed = parseNumber(parts[1]);
          }
        }
      }
    }
  }
  
  return revenue as Revenue;
}

/**
 * Parse retention metrics
 */
function parseRetention(lines: string[]): Retention {
  const retention: Partial<Retention> = {
    threeMonthCompletionRate: 0,
    churnRate: 0,
    averageLifetimeValue: 0,
  };
  
  let inRetentionSection = false;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    if (line.includes('Retention & Churn')) {
      inRetentionSection = true;
      continue;
    }
    
    if (line.includes('---') && inRetentionSection) {
      inRetentionSection = false;
    }
    
    if (inRetentionSection) {
      if (line.includes('3-Month Completion Rate')) {
        const match = line.match(/(\d+)%/);
        if (match) {
          retention.threeMonthCompletionRate = parsePercentage(match[1]);
        }
      }
      
      if (line.includes('Churn Rate')) {
        const match = line.match(/(\d+)%/);
        if (match) {
          retention.churnRate = parsePercentage(match[1]);
        }
      }
      
      if (line.includes('Auto-Renew Take-Up')) {
        const match = line.match(/(\d+)%/);
        if (match) {
          retention.autoRenewTakeUp = parsePercentage(match[1]);
        }
      }
      
      if (line.includes('Average Lifetime Value')) {
        const match = line.match(/£(\d+)/);
        if (match) {
          retention.averageLifetimeValue = parseNumber(match[1]);
        }
      }
    }
  }
  
  return retention as Retention;
}

/**
 * Parse halo effect metrics
 */
function parseHalo(lines: string[]): Halo {
  const halo: Partial<Halo> = {
    footfallUplift: 0,
    estimatedIncrementalStoreRevenue: 0,
  };
  
  let inHaloSection = false;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    if (line.includes('Store Halo Effect')) {
      inHaloSection = true;
      continue;
    }
    
    if (line.includes('---') && inHaloSection) {
      inHaloSection = false;
    }
    
    if (inHaloSection) {
      if (line.includes('Footfall Uplift')) {
        const match = line.match(/\+?(\d+)%/);
        if (match) {
          halo.footfallUplift = parsePercentage(match[1]);
        }
      }
      
      if (line.includes('Gift Card Redemption')) {
        const match = line.match(/(\d+)%/);
        if (match) {
          halo.giftCardRedemption = parsePercentage(match[1]);
        }
      }
      
      if (line.includes('Same-Day Cross Purchase')) {
        const match = line.match(/(\d+)%/);
        if (match) {
          halo.sameDayCrossPurchase = parsePercentage(match[1]);
        }
      }
      
      if (line.includes('Estimated Incremental Store Revenue')) {
        const match = line.match(/£([\d,]+)/);
        if (match) {
          halo.estimatedIncrementalStoreRevenue = parseNumber(match[1]);
        }
      }
    }
  }
  
  return halo as Halo;
}

/**
 * Parse time of day metrics
 */
function parseTimeOfDay(lines: string[]): TimeOfDay {
  const timeOfDay: Partial<TimeOfDay> = {
    sales10am1pm: 0,
    sales1pm4pm: 0,
    sales4pm7pm: 0,
    sales7pm9pm: 0,
    peakWindow: '4pm-7pm',
  };
  
  let inTimeSection = false;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    if (line.includes('Sales by Time of Day')) {
      inTimeSection = true;
      continue;
    }
    
    if (line.includes('---') && inTimeSection) {
      inTimeSection = false;
    }
    
    if (inTimeSection) {
      if (line.includes('10am') && line.includes('1pm')) {
        const match = line.match(/(\d+)%/);
        if (match) {
          timeOfDay.sales10am1pm = parsePercentage(match[1]);
        }
      }
      
      if (line.includes('1pm') && line.includes('4pm')) {
        const match = line.match(/(\d+)%/);
        if (match) {
          timeOfDay.sales1pm4pm = parsePercentage(match[1]);
        }
      }
      
      if (line.includes('4pm') && line.includes('7pm')) {
        const match = line.match(/(\d+)%/);
        if (match) {
          timeOfDay.sales4pm7pm = parsePercentage(match[1]);
        }
      }
      
      if (line.includes('7pm') && line.includes('9pm')) {
        const match = line.match(/(\d+)%/);
        if (match) {
          timeOfDay.sales7pm9pm = parsePercentage(match[1]);
        }
      }
      
      if (line.includes('Peak conversion window')) {
        const match = line.match(/(\d+pm--\d+pm)/);
        if (match) {
          timeOfDay.peakWindow = match[1].replace('--', '-');
        }
      }
    }
  }
  
  return timeOfDay as TimeOfDay;
}

/**
 * Parse feedback metrics
 */
function parseFeedback(lines: string[]): Feedback {
  const feedback: Partial<Feedback> = {
    overallExperienceScore: 0,
    wouldRecommend: 0,
    commonThemes: [],
  };
  
  let inFeedbackSection = false;
  let inThemesSection = false;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    if (line.includes('Customer Feedback')) {
      inFeedbackSection = true;
      continue;
    }
    
    if (line.includes('Common Feedback Themes')) {
      inThemesSection = true;
      continue;
    }
    
    if (line.includes('---') && inFeedbackSection) {
      inFeedbackSection = false;
      inThemesSection = false;
    }
    
    if (inFeedbackSection && !inThemesSection) {
      if (line.includes('Overall Experience Score')) {
        const match = line.match(/(\d+\.?\d*)\s*\/\s*5/);
        if (match) {
          feedback.overallExperienceScore = parseFloat(match[1]);
        }
      }
      
      if (line.includes('Ease of Purchase')) {
        const match = line.match(/(\d+\.?\d*)\s*\/\s*5/);
        if (match) {
          feedback.easeOfPurchaseScore = parseFloat(match[1]);
        }
      }
      
      if (line.includes('Gift Appeal Rating')) {
        const match = line.match(/(\d+\.?\d*)\s*\/\s*5/);
        if (match) {
          feedback.giftAppealRating = parseFloat(match[1]);
        }
      }
      
      if (line.includes('Would Recommend')) {
        const match = line.match(/(\d+)%/);
        if (match) {
          feedback.wouldRecommend = parsePercentage(match[1]);
        }
      }
    }
    
    if (inThemesSection && line.startsWith('-')) {
      const theme = line.replace(/^-\s*/, '').replace(/"/g, '').trim();
      if (theme && !theme.includes('---')) {
        feedback.commonThemes!.push(theme);
      }
    }
  }
  
  return feedback as Feedback;
}

/**
 * Parse strategic takeaways
 */
function parseStrategicTakeaways(lines: string[]): StrategicTakeaways | undefined {
  const points: string[] = [];
  let inTakeawaysSection = false;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    
    if (line.includes('Strategic Takeaways')) {
      inTakeawaysSection = true;
      continue;
    }
    
    if (line.includes('---') && inTakeawaysSection) {
      break;
    }
    
    if (inTakeawaysSection && line.startsWith('-')) {
      const point = line.replace(/^-\s*/, '').trim();
      if (point) {
        points.push(point);
      }
    }
  }
  
  return points.length > 0 ? { points } : undefined;
}

/**
 * Check if customReportData contains markdown-structured data
 */
export function isMarkdownStructuredReport(customReportData: string | null | undefined): boolean {
  if (!customReportData) return false;
  
  try {
    // Check if it's a JSON string that might contain markdown
    const parsed = typeof customReportData === 'string' ? JSON.parse(customReportData) : customReportData;
    
    // If it's an object, check for markdown-like structure
    if (typeof parsed === 'object' && parsed !== null) {
      // Check if it has markdown text or structured sections
      const text = JSON.stringify(parsed).toLowerCase();
      return text.includes('footfall') && text.includes('revenue') && text.includes('conversion');
    }
    
    // If it's a string, check for markdown patterns
    if (typeof parsed === 'string') {
      return parsed.includes('##') || parsed.includes('Footfall & Visibility') || parsed.includes('Campaign Performance');
    }
    
    return false;
  } catch {
    // If parsing fails, check if it's raw markdown text
    const text = customReportData.toString();
    return text.includes('##') || text.includes('Footfall & Visibility') || text.includes('Campaign Performance');
  }
}
